// Theme registry — user-selectable named palettes (IDE/terminal-style).
//
// The canonical design-tokens.ts still owns the "brand" palette (its
// `palette`/`intensityScale`/`heatmapRamp` exports stay the single source for
// anything that doesn't opt into theming). This module widens that into a
// FAMILY × MODE registry: each family fills the exact same ~30 role contract
// (20 palette roles + 5-band intensity scale + 5-step heatmap ramp) in both a
// light and a dark mode, so the resolver can pick `themes[family][mode]`.
//
// Each family lives one-per-file under ./themes/ (split out of this file
// 2026-08-24 to keep it under the 500-line cap); the contract types are in
// ./themes/types. Adding a family = new ./themes/<name>.ts + extend
// THEME_FAMILY_KEYS + add it to the `themes` map + its label in THEME_FAMILIES.

import {
  palette,
  intensityScale,
  heatmapRamp,
  effortRamp,
} from "./design-tokens.js";
import type { ThemeDefinition } from "./themes/types.js";
import { catppuccin } from "./themes/catppuccin.js";
import { rosepine } from "./themes/rosepine.js";
import { metria } from "./themes/metria.js";

// Re-export the contract types so `@jackedlogs/shared` consumers are unchanged.
export type {
  Hex,
  ThemePalette,
  ThemeIntensity,
  EffortRampStop,
  HeatmapRamp,
  ThemeDefinition,
} from "./themes/types.js";

// Brand = the existing canonical tokens, unchanged (referenced, not copied, so
// design-tokens.ts stays the one place brand hues are edited).
const brand: ThemeDefinition = { palette, intensityScale, heatmapRamp, effortRamp };

// Family keys as a literal tuple so the zod enum (schemas/profile.ts) and the
// registry stay a single source of truth. Adding a family = extend this tuple +
// add its ThemeDefinition module; the schema/API pick it up for free.
export const THEME_FAMILY_KEYS = ["brand", "catppuccin", "rosepine", "metria"] as const;
export type ThemeFamily = (typeof THEME_FAMILY_KEYS)[number];

/** User-facing mode preference. `system` follows the OS colour scheme. */
export const THEME_MODES = ["system", "light", "dark"] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const themes: Record<ThemeFamily, ThemeDefinition> = { brand, catppuccin, rosepine, metria };

/** The persisted user preference (Profile.themePreference). */
export type ThemePreference = { family: ThemeFamily; mode: ThemeMode };

/** Ordered list for the Settings picker (label = display name). */
export const THEME_FAMILIES: { key: ThemeFamily; label: string }[] = [
  { key: "catppuccin", label: "Catppuccin" },
  // ⭐ La CHIAVE resta `brand`: e' il valore persistito in Profile.themePreference,
  // quindi il rebrand non migra niente. Cambia solo l'etichetta, che e' display-only.
  // ⚠️ 23/08: l'etichetta "Metria" e' PASSATA alla famiglia nuova, quella
  // terracotta. Questa resta l'arancione storico di JackedLogs e prende un nome
  // che descrive il colore invece del prodotto — sua decisione: «lo teniamo se
  // e' comunque usabile e bello e lo rinominiamo in altro».
  { key: "brand", label: "Ambra" },
  { key: "metria", label: "Metria" },
  { key: "rosepine", label: "Rosé Pine" },
];

// The product default palette for a brand-new install (no persisted preset).
// Metria is the app's brand family (23/08 rebrand); a fresh boot lands on it,
// and `coerceThemePreference` falls back to it field-wise for stale/partial
// caches. Only ever a FALLBACK — a saved user pick always wins.
export const DEFAULT_THEME_FAMILY: ThemeFamily = "metria";
export const DEFAULT_THEME_MODE: ThemeMode = "system";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = {
  family: DEFAULT_THEME_FAMILY,
  mode: DEFAULT_THEME_MODE,
};

/** Narrowing guards for values coming off the wire / persisted preference. */
export function isThemeFamily(v: unknown): v is ThemeFamily {
  return typeof v === "string" && (THEME_FAMILY_KEYS as readonly string[]).includes(v);
}
export function isThemeMode(v: unknown): v is ThemeMode {
  return typeof v === "string" && (THEME_MODES as readonly string[]).includes(v);
}

/** Resolve the effective dark flag from a mode + the OS colour scheme. */
export function resolveDark(mode: ThemeMode, osDark: boolean): boolean {
  return mode === "system" ? osDark : mode === "dark";
}

/** Coerce an unknown (cache / wire) into a valid ThemePreference, field-wise. */
export function coerceThemePreference(v: unknown): ThemePreference {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return {
    family: isThemeFamily(o.family) ? o.family : DEFAULT_THEME_FAMILY,
    mode: isThemeMode(o.mode) ? o.mode : DEFAULT_THEME_MODE,
  };
}
